import React from 'react';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { InfoBlock } from '@picsio/ui';
import { Interweave } from 'interweave';
import { reloadApp, reloadPage } from '../../../helpers/history';
import { showDialog, showErrorDialog } from '../../../components/dialog';
import Toast from '../../../components/Toast';
import sanitizeXSS from '../../../shared/sanitizeXSS';
import { showValidateCardForm, VALIDATE_CARD_DIALOG_CANCELLED } from '../../helpers/billing';
import { trackAddonRemovedEvents } from '../../../helpers/intercom/churnRiskEvents';

const subscribe = createAsyncThunk(
  'billing/subscribe',
  async (
    payload,
    {
      getState, rejectWithValue, extra: {
        sdk, utils, localization, UiBlocker, Logger,
      },
    },
  ) => {
    const addonsFeatures = getState().user.addonsFeatures || {};

    Logger.log('User', 'SettingsBillingChangeSubscribe');
    const {
      plan, totalAccount, removedAddons = [], ...data
    } = payload;

    const { addons } = totalAccount;

    const getAddon = (feature) => addons.find((addon) => addon.featureName === feature);

    const legacyAiKit = getAddon('aiKit');
    const videoIntelligence = getAddon('videoIntelligence');
    const imageIntelligence = getAddon('imageIntelligence');
    const isValidIntelligenceId = (id) => Boolean(id && id !== 'false' && id !== 'true');

    /** normalize addon case if added new imageIntelligence or videoIntelligence need to remove from stripe old/legacy aiKit from subscriptions */
    const normalizedTotalDataAddons = (isValidIntelligenceId(videoIntelligence?.id) || isValidIntelligenceId(imageIntelligence?.id)) && isValidIntelligenceId(legacyAiKit?.id)
      ? addons.map((addon) => (addon.featureName === 'aiKit' ? { ...addon, value: false } : addon))
      : addons;

    const hasVideoIntelligence = (videoIntelligence?.value === true && isValidIntelligenceId(videoIntelligence?.id));
    const hasImageIntelligence = (imageIntelligence?.value === true && isValidIntelligenceId(imageIntelligence?.id));

    const isAddedOrUpdatedVideoIntelligence = (!addonsFeatures?.videoIntelligence?.id && hasVideoIntelligence) || (hasVideoIntelligence && videoIntelligence?.id !== addonsFeatures.videoIntelligence?.id);
    const isAddedOrUpdatedImageIntelligence = (!addonsFeatures?.imageIntelligence?.id && hasImageIntelligence) || (hasImageIntelligence && imageIntelligence?.id !== addonsFeatures.imageIntelligence?.id);
    const isIntelligenceChanged = isAddedOrUpdatedVideoIntelligence || isAddedOrUpdatedImageIntelligence;
    const isRemovedImageIntelligence = (imageIntelligence?.value === false && !!addonsFeatures?.imageIntelligence?.id);
    const isRemovedVideoIntelligence = (videoIntelligence?.value === false && !!addonsFeatures?.videoIntelligence?.id);

    data.addons = normalizedTotalDataAddons;
    const {
      name: planName, amount,
    } = plan;
    const { sum } = totalAccount;
    const stripePlan = {
      panelLabel: localization.BILLING.checkoutSubscribe,
      name: planName,
      amount,
    };
    const { user } = getState();
    const {
      customer: { card },
    } = user;

    try {
      UiBlocker.block(localization.BILLING.textPaymentLoadingDetails);

      if (!card) {
        const { id: tokenId } = await showValidateCardForm({
          user,
          plan: stripePlan,
          amount: sum * 100,
          formTitle: localization.BILLING.addCardDetails,
        });

        if (tokenId) {
          data.tokenId = tokenId;
        } else {
          throw new Error('tokenId is undefined');
        }
      }

      UiBlocker.block(localization.BILLING.textPaymentFinishing);

      const { data: subscription } = await sdk.billing.subscribe(data);

      trackAddonRemovedEvents(user, removedAddons);

      Logger.log('User', 'SubscriptionChanged', planName);
      const userDialogueMessage = localization.BILLING.textTextSubcriptionUpdated;
      const isRemovedIntelligence = isRemovedImageIntelligence || isRemovedVideoIntelligence;
      const showIntelligenceDialog = isIntelligenceChanged || isRemovedIntelligence;
      const showCancelBtn = isIntelligenceChanged && !isRemovedImageIntelligence && !isRemovedVideoIntelligence;

      const intelligenceBtnOkText = isRemovedIntelligence
        ? localization.BILLING.textBtnOk
        : localization.BILLING.textBtnGoToEnableVisualSearch;

      const textBtnOk = showIntelligenceDialog
        ? intelligenceBtnOkText
        : localization.BILLING.textBtnGoToLibrary;

      const textBtnCancel = showCancelBtn
        ? localization.BILLING.textBtnSkipForNow
        : null;

      const getRemovalIntelligenceText = () => {
        if (isRemovedImageIntelligence && isRemovedVideoIntelligence) {
          return localization.BILLING.textRemovalBothAiKit;
        }
        if (isRemovedImageIntelligence) {
          return localization.BILLING.textRemovalImageAiKit;
        }
        if (isRemovedVideoIntelligence) {
          return localization.BILLING.textRemovalVideoAiKit;
        }
        return '';
      };

      const dialogBody = (
        <div>
          <div>
            <Interweave content={sanitizeXSS(userDialogueMessage)} />
          </div>
          <br />
          <InfoBlock>
            {isRemovedIntelligence ? getRemovalIntelligenceText() : localization.BILLING.textActivateVisualSearch}
          </InfoBlock>
          <br />
        </div>
      );

      const dialogData = showIntelligenceDialog
        ? { children: dialogBody }
        : { text: userDialogueMessage };

      Logger.log('UI', 'ChangeSubscribeSuccessDialog', { userDialogueMessage });

      const onOk = () => {
        Logger.log('User', 'SubscriptionUpdatedChooseNextAction', { action_chosen: textBtnOk });

        return showCancelBtn ? reloadPage('/teammates?tab=ai') : reloadApp();
      };

      const onCancel = () => {
        Logger.log('User', 'SubscriptionUpdatedChooseNextAction', { action_chosen: textBtnCancel });

        return isIntelligenceChanged ? reloadApp() : reloadPage('/billing?tab=overview');
      };

      Logger.log('User', 'SettingsBillingSubscriptUpdatedModalShown');

      showDialog({
        title: localization.BILLING.textTitleThanks,
        textBtnCancel,
        textBtnOk,
        ...dialogData,
        onOk,
        onCancel,
      });

      UiBlocker.block(localization.BILLING.textDetailsSaving);

      return {
        subscription,
      };
    } catch (err) {
      if (err?.message === VALIDATE_CARD_DIALOG_CANCELLED) {
        return rejectWithValue(err);
      }
      const errorSubcode = utils.getDataFromResponceError(err, 'subcode');
      const errorMessage = utils.getDataFromResponceError(err, 'msg');
      const errorReason = utils.getDataFromResponceError(err, 'reason');
      let userDialogueMessage = '';
      let isErrorShown = false;
      if (errorSubcode === 'CardError') {
        Logger.log('User', 'ChangeSubscribeFailedDialog', { userDialogueMessage });
        showErrorDialog(localization.BILLING.stripeCardErrors[errorReason] || localization.BILLING.stripeCardErrors.default, errorMessage);
        isErrorShown = true;
      }
      if (errorSubcode === 'NextPlanLimitsError') {
        userDialogueMessage = localization.BILLING.DIALOG_LIMITS_EXCEEDED.text;
        const dialogData = {
          title: localization.BILLING.DIALOG_LIMITS_EXCEEDED.title,
          text: userDialogueMessage,
          textBtnCancel: localization.DIALOGS.btnOk,
          textBtnOk: null,
        };
        if (user?.subscriptionFeatures?.chatSupport) {
          dialogData.textBtnCancel = localization.DIALOGS.btnCancel;
          dialogData.textBtnOk = localization.BILLING.DIALOG_LIMITS_EXCEEDED.btnOk;
          dialogData.onOk = () => window.dispatchEvent(new Event('toolbar:ui:liveSupport'));
          dialogData.id = 'itemliveSupport';
        }
        Logger.log('User', 'ChangeSubscribeFailedDialog', { userDialogueMessage });
        showDialog(dialogData);
        isErrorShown = true;
      }
      if (err.message === 'tokenId is undefined') {
        userDialogueMessage = localization.BILLING.textCardCantCheck;
        Logger.log('User', 'ChangeSubscribeFailedDialog', { userDialogueMessage });
        showErrorDialog(userDialogueMessage);
        isErrorShown = true;
      }
      if (!isErrorShown) {
        userDialogueMessage = localization.BILLING.textSubscriptionCantUpdate;
        Logger.log('User', 'ChangeSubscribeFailedDialog', { userDialogueMessage });
        Toast(userDialogueMessage, { autoClose: false, type: 'error' });
      }
      Logger.error(new Error('Subscribe to plan failed'), { error: err }, [
        'SubscribeBillingPlanFailed',
        { errorMessage: (err && err.message) || 'NoMessage', userDialogueMessage },
      ]);
      return rejectWithValue(err);
    } finally {
      UiBlocker.unblock();
    }
  },
);

export default subscribe;
